import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Tabs, Tab,
    TextField, MenuItem, Typography, Alert, Stack, CircularProgress,
} from "@mui/material";
import { FileText, Upload, Trash2, ExternalLink } from "lucide-react";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";

const BASE = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;

// Section score inputs per test family — mirrors prepExamScore's score_1..4 mapping.
const SECTION_CONFIG = {
    dsat: [{ k: "score_1", label: "Reading & Writing" }, { k: "score_2", label: "Math" }],
    sat: [{ k: "score_1", label: "Reading & Writing" }, { k: "score_2", label: "Math" }],
    psat: [{ k: "score_1", label: "Reading & Writing" }, { k: "score_2", label: "Math" }],
    act: [
        { k: "score_1", label: "English" }, { k: "score_2", label: "Math" },
        { k: "score_3", label: "Reading" }, { k: "score_4", label: "Science", optional: true },
    ],
    ap: [{ k: "score_1", label: "Score (1–5)" }],
};
const sectionsFor = (qb) => SECTION_CONFIG[(qb || "").toLowerCase()] || SECTION_CONFIG.dsat;

const EMPTY_SCORES = { score_1: "", score_2: "", score_3: "", score_4: "" };

// Add / edit preserved score history for one exam (opened from the exam-row pencil). The student
// is picked inside; history for a student comes from the matrix cells already loaded upstream.
const ExamHistoryModal = ({ open, onClose, programUuid, exam, students, qbType, cells, onSaved }) => {
    const sections = useMemo(() => sectionsFor(qbType), [qbType]);
    const [tab, setTab] = useState("add");
    const [studentId, setStudentId] = useState("");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const [takenOn, setTakenOn] = useState("");
    const [tryNumber, setTryNumber] = useState("");
    const [scores, setScores] = useState(EMPTY_SCORES);
    const [pdfFile, setPdfFile] = useState(null);           // newly-selected File (staged until Save)
    const [existingPdfUrl, setExistingPdfUrl] = useState(null);
    const [editId, setEditId] = useState("");

    const historyForStudent = useMemo(
        () => (studentId && cells && cells[studentId] && cells[studentId][exam?.id]?.history) || [],
        [studentId, cells, exam]
    );

    // Reset the form on open / tab / student change. Add tab prefills try# = existing + 1.
    useEffect(() => {
        if (!open) return;
        setErr(""); setEditId(""); setPdfFile(null); setExistingPdfUrl(null);
        setScores(EMPTY_SCORES); setTakenOn("");
        setTryNumber(tab === "add" ? String((historyForStudent.length || 0) + 1) : "");
    }, [open, tab, studentId, historyForStudent.length]);

    const loadEntry = (id) => {
        setEditId(id);
        const e = historyForStudent.find((h) => String(h.id) === String(id));
        if (!e) return;
        setTakenOn(e.taken_on || "");
        setTryNumber(String(e.try_number ?? ""));
        setScores({
            score_1: e.score_1 ?? "", score_2: e.score_2 ?? "",
            score_3: e.score_3 ?? "", score_4: e.score_4 ?? "",
        });
        setPdfFile(null);
        setExistingPdfUrl(e.pdf_url || null);
    };

    const setScore = (k, v) => setScores((s) => ({ ...s, [k]: v }));

    const missingRequired =
        !studentId || !takenOn || !String(tryNumber).trim() ||
        sections.some((sec) => !sec.optional && String(scores[sec.k]).trim() === "");
    const hasPdf = !!pdfFile || (tab === "edit" && !!existingPdfUrl);
    // Always render the form fields (so the modal doesn't resize), but disable them until the
    // context is chosen: a student on the Add tab, a history entry on the Edit tab.
    const showForm = true;
    const formDisabled = tab === "add" ? !studentId : !editId;
    const canSave = !missingRequired && !saving && !formDisabled;

    const scorePayload = () => {
        const out = {};
        ["score_1", "score_2", "score_3", "score_4"].forEach((k) => {
            const v = String(scores[k]).trim();
            out[k] = v === "" ? null : Number(v);
        });
        return out;
    };

    const save = async () => {
        setErr(""); setSaving(true);
        try {
            const cfg = { headers: { Authorization: getAuthHeader() } };
            // Large PDFs bypass the JSON body limit: upload straight to S3 via a presigned URL,
            // then send only the resulting key.
            let pdf_key;
            if (pdfFile) {
                const studentName = (students || []).find((s) => String(s.student_id) === String(studentId))?.name || "";
                const { data } = await Axios.post(
                    `${BASE}/${programUuid}/exam-history/upload-url`,
                    { student_name: studentName, exam_name: exam?.exam_title, try_number: Number(tryNumber), taken_on: takenOn },
                    cfg
                );
                // Direct-to-S3 PUT — send ONLY Content-Type (the ACL is signed into the URL), and
                // no auth header. Mirrors the working FRQ upload in TestSubmitPage.jsx.
                await Axios.put(data.uploadUrl, pdfFile, { headers: { "Content-Type": "application/pdf" } });
                pdf_key = data.key;
            }
            const body = {
                exam_id: exam.id, try_number: Number(tryNumber), taken_on: takenOn,
                ...scorePayload(), ...(pdf_key ? { pdf_key } : {}),
            };
            if (tab === "add") {
                await Axios.post(`${BASE}/${programUuid}/exam-history`, { ...body, student_id: studentId }, cfg);
            } else {
                await Axios.put(`${BASE}/${programUuid}/exam-history/${editId}`, body, cfg);
            }
            onSaved && onSaved();
            onClose();
        } catch (e) {
            console.error("Save exam history error:", e);
            setErr(e?.response?.data?.error || e?.message || "Failed to save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!editId) return;
        setSaving(true);
        try {
            await Axios.delete(`${BASE}/${programUuid}/exam-history/${editId}`, { headers: { Authorization: getAuthHeader() } });
            onSaved && onSaved();
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.error || "Failed to delete.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Score history{exam ? ` — ${exam.exam_title}` : ""}</DialogTitle>
            <DialogContent dividers>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                    <Tab value="add" label="Add" />
                    <Tab value="edit" label="Edit" />
                </Tabs>

                <Stack spacing={2}>
                    <TextField select size="small" label="Student" value={studentId} fullWidth
                        onChange={(e) => setStudentId(e.target.value)}>
                        {(students || []).map((s) => (
                            <MenuItem key={s.student_id} value={s.student_id}>{s.name || s.email}</MenuItem>
                        ))}
                    </TextField>

                    {tab === "edit" && studentId && (
                        historyForStudent.length ? (
                            <TextField select size="small" label="History entry" value={editId} fullWidth
                                onChange={(e) => loadEntry(e.target.value)}>
                                {historyForStudent.map((h) => (
                                    <MenuItem key={h.id} value={h.id}>
                                        Try {h.try_number} · {h.taken_on} · {h.score_total}{h.detail ? ` (${h.detail})` : ""}
                                    </MenuItem>
                                ))}
                            </TextField>
                        ) : <Alert severity="info">No history entries for this student yet.</Alert>
                    )}

                    {showForm && (
                        <>
                            {formDisabled && (
                                <Typography variant="caption" color="text.secondary">
                                    {tab === "add" ? "Select a student to fill in the rest." : "Select a history entry to edit."}
                                </Typography>
                            )}
                            <Box sx={{ display: "flex", gap: 2 }}>
                                <TextField size="small" type="date" label="Date" InputLabelProps={{ shrink: true }}
                                    value={takenOn} onChange={(e) => setTakenOn(e.target.value)} fullWidth disabled={formDisabled} />
                                <TextField size="small" type="number" label="Try #" value={tryNumber}
                                    onChange={(e) => setTryNumber(e.target.value)} sx={{ width: 110 }} disabled={formDisabled} />
                            </Box>

                            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                {sections.map((sec) => (
                                    <TextField key={sec.k} size="small" type="number"
                                        label={sec.label + (sec.optional ? " · optional" : "")}
                                        value={scores[sec.k]} onChange={(e) => setScore(sec.k, e.target.value)}
                                        sx={{ flex: "1 1 120px" }} disabled={formDisabled} />
                                ))}
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Button component="label" size="small" variant="outlined" startIcon={<Upload size={16} />} disabled={formDisabled}>
                                    {pdfFile || existingPdfUrl ? "Replace PDF" : "Upload PDF"}
                                    <input hidden type="file" accept="application/pdf"
                                        onChange={(e) => setPdfFile(e.target.files?.[0] || null)} />
                                </Button>
                                {pdfFile ? (
                                    <Typography variant="caption" color="text.secondary">
                                        <FileText size={13} style={{ verticalAlign: "-2px" }} /> {pdfFile.name} — saves on Save
                                    </Typography>
                                ) : existingPdfUrl ? (
                                    <Typography variant="caption" component="a" href={existingPdfUrl} target="_blank"
                                        rel="noreferrer" sx={{ color: "info.main", textDecoration: "none" }}>
                                        <FileText size={13} style={{ verticalAlign: "-2px" }} /> View current PDF{" "}
                                        <ExternalLink size={11} style={{ verticalAlign: "-1px" }} />
                                    </Typography>
                                ) : null}
                            </Box>

                            {!hasPdf && <Alert severity="warning" sx={{ py: 0.25 }}>PDF is missing — you can still proceed.</Alert>}
                        </>
                    )}

                    {err && <Alert severity="error">{err}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                {tab === "edit" && editId && (
                    <Button color="error" startIcon={<Trash2 size={16} />} onClick={remove} disabled={saving} sx={{ mr: "auto" }}>
                        Delete
                    </Button>
                )}
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={!canSave}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExamHistoryModal;
